import scrollIntoView from 'scroll-into-view-if-needed';
import { ARENA_SELECTOR } from '../../const';

const SCROLL_IDLE_THRESHOLD_MS = 80;
const SCROLL_FALLBACK_TIMEOUT_MS = 800;

/**
 * Scrolls an element into view smoothly, centering it in the viewport or container.
 * Uses scroll-into-view-if-needed library for smooth scrolling with proper container detection.
 *
 * @param element - The target element to scroll to
 * @param containerEl - Optional container element for builder/preview modes
 * @returns Promise that resolves when scrolling is complete
 */
export function scrollToElement(
  element: HTMLElement,
  containerEl?: HTMLElement | null,
): Promise<void> {
  return new Promise((resolve) => {
    // For builder/preview modes, find the scrollable container (arena)
    const scrollContainer = containerEl?.closest(ARENA_SELECTOR) as HTMLElement | null;
    const scrollTarget: HTMLElement | Window = scrollContainer || window;

    let resolved = false;
    let idleTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let fallbackTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let removeScrollListener: (() => void) | null = null;

    const finish = () => {
      if (resolved) {
        return;
      }
      resolved = true;
      if (idleTimeoutId) {
        clearTimeout(idleTimeoutId);
      }
      if (fallbackTimeoutId) {
        clearTimeout(fallbackTimeoutId);
      }
      removeScrollListener?.();
      resolve();
    };

    const scheduleFinishAfterIdle = () => {
      if (idleTimeoutId) {
        clearTimeout(idleTimeoutId);
      }
      idleTimeoutId = setTimeout(finish, SCROLL_IDLE_THRESHOLD_MS);
    };

    const handleScrollEnd = () => {
      scheduleFinishAfterIdle();
    };

    scrollIntoView(element, {
      scrollMode: 'if-needed',
      block: 'center',
      inline: 'center',
      behavior: 'smooth',
      // Use the arena container for builder/preview, or let the library find the scrollable parent for runner
      boundary: scrollContainer || undefined,
    });

    scrollTarget.addEventListener('scroll', handleScrollEnd, { passive: true });
    removeScrollListener = () => {
      scrollTarget.removeEventListener('scroll', handleScrollEnd);
    };

    // Wait for smooth scroll animation to complete (typically 300-500ms)
    // Resolve as soon as scroll settles with a short idle threshold, keep a fallback timeout
    scheduleFinishAfterIdle();
    fallbackTimeoutId = setTimeout(finish, SCROLL_FALLBACK_TIMEOUT_MS);
  });
}
